import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './ManageServiceItems.css';

interface ServiceItem {
  id: string;
  name: string;
  subcategoryId: string;
  price: number;
  duration: string;
  description: string;
  jobDetails: string;
  order?: number;
  active?: boolean;
}

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
  icon?: string;
  order?: number;
  commissionRate?: number;
}

interface GroupedServiceItems {
  [subcategoryId: string]: ServiceItem[];
}

interface NewServiceItem {
  name: string;
  price: number;
  duration: string;
  description: string;
  jobDetails: string;
}

const ManageServiceItems: React.FC = () => {
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [filteredServiceItems, setFilteredServiceItems] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [updating, setUpdating] = useState<string | null>(null);
  const defaultShowPerGroup = 50;
  const [groupShowCounts, setGroupShowCounts] = useState<Record<string, number>>({});
  
  // States for grouping
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  
  // States for add modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [newServiceItem, setNewServiceItem] = useState<NewServiceItem>({
    name: '',
    price: 0,
    duration: '',
    description: '',
    jobDetails: ''
  });
  const [addingServiceItem, setAddingServiceItem] = useState(false);

  // States for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingServiceItem, setEditingServiceItem] = useState<ServiceItem | null>(null);
  const [updatingServiceItem, setUpdatingServiceItem] = useState(false);

  const fetchSubcategories = async () => {
    try {
      console.log('Bắt đầu tải danh sách dịch vụ...');
      
      let subcategoriesQuery;
      let querySnapshot;

      try {
        subcategoriesQuery = query(
          collection(db, 'subcategories'),
          orderBy('order', 'asc')
        );
        console.log('Thử query subcategories với orderBy order...');
        querySnapshot = await getDocs(subcategoriesQuery);
      } catch (indexError) {
        console.warn('Query subcategories với orderBy thất bại, thử query đơn giản:', indexError);
        subcategoriesQuery = collection(db, 'subcategories');
        console.log('Thử query subcategories đơn giản...');
        querySnapshot = await getDocs(subcategoriesQuery);
      }

      const subcategoriesData: Subcategory[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Subcategory doc:', doc.id, data);
        
        subcategoriesData.push({
          id: doc.id,
          name: data.name || '',
          categoryId: data.categoryId || '',
          icon: data.icon || '',
          order: data.order || 0,
          commissionRate: data.commissionRate || 0
        });
      });

      // Sắp xếp thủ công theo order nếu cần
      if (subcategoriesData.length > 0) {
        subcategoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${subcategoriesData.length} dịch vụ`);
      setSubcategories(subcategoriesData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách dịch vụ:', error);
      setSubcategories([]);
    }
  };

  const fetchServiceItems = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách gói dịch vụ...');

      let serviceItemsQuery;
      let querySnapshot;

      try {
        // Thử query với orderBy theo trường order trước
        serviceItemsQuery = query(
          collection(db, 'service_items'),
          orderBy('order', 'asc')
        );
        console.log('Thử query với orderBy order...');
        querySnapshot = await getDocs(serviceItemsQuery);
      } catch (indexError) {
        console.warn('Query với orderBy thất bại, thử query đơn giản:', indexError);
        
        // Nếu lỗi index, thử query đơn giản hơn
        serviceItemsQuery = collection(db, 'service_items');
        console.log('Thử query đơn giản...');
        querySnapshot = await getDocs(serviceItemsQuery);
      }

      const serviceItemsData: ServiceItem[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Service item doc:', doc.id, data);
        
        serviceItemsData.push({
          id: doc.id,
          name: data.name || '',
          subcategoryId: data.subcategoryId || '',
          price: data.price || 0,
          duration: data.duration || '',
          description: data.description || '',
          jobDetails: data.jobDetails || '',
          order: data.order || 0,
          active: data.active ?? true // Mặc định là true nếu không có giá trị
        });
      });

      // Sắp xếp thủ công theo order nếu cần
      if (serviceItemsData.length > 0) {
        serviceItemsData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${serviceItemsData.length} gói dịch vụ`);
      setServiceItems(serviceItemsData);
      setFilteredServiceItems(serviceItemsData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách gói dịch vụ:', error);
      
      let errorMessage = 'Không thể tải danh sách gói dịch vụ';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụ. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const subcategoryNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of subcategories) map[s.id] = s.name || '';
    return map;
  }, [subcategories]);

  const getSubcategoryName = (subcategoryId: string): string => {
    return subcategoryNameMap[subcategoryId] || subcategoryId || 'Không xác định';
  };

  const applyFilters = (serviceItemsToFilter: ServiceItem[]) => {
    let filtered = serviceItemsToFilter;

    // Filter theo tên, mô tả, subcategoryId hoặc tên subcategory
    if (nameFilter.trim()) {
      filtered = filtered.filter(serviceItem => {
        const subcategoryName = getSubcategoryName(serviceItem.subcategoryId);
        return (
          serviceItem.name.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          serviceItem.description.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          serviceItem.jobDetails.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          serviceItem.duration.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          serviceItem.subcategoryId.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          subcategoryName.toLowerCase().includes(nameFilter.trim().toLowerCase())
        );
      });
    }

    return filtered;
  };

  const groupServiceItemsBySubcategory = (serviceItemsToGroup: ServiceItem[]): GroupedServiceItems => {
    const grouped: GroupedServiceItems = {};
    for (const serviceItem of serviceItemsToGroup) {
      const subcategoryId = serviceItem.subcategoryId || 'unknown';
      if (!grouped[subcategoryId]) grouped[subcategoryId] = [];
      grouped[subcategoryId].push(serviceItem);
    }
    Object.keys(grouped).forEach((subcategoryId) => {
      grouped[subcategoryId].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return grouped;
  };

  const toggleGroupCollapse = (subcategoryId: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(subcategoryId)) {
      newCollapsed.delete(subcategoryId);
    } else {
      newCollapsed.add(subcategoryId);
    }
    setCollapsedGroups(newCollapsed);
  };

  // Functions for add modal
  const openAddModal = (subcategoryId: string) => {
    setSelectedSubcategoryId(subcategoryId);
    setIsModalOpen(true);
  };

  const closeAddModal = () => {
    setSelectedSubcategoryId(null);
    setIsModalOpen(false);
    setNewServiceItem({
      name: '',
      price: 0,
      duration: '',
      description: '',
      jobDetails: ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewServiceItem(prev => ({
      ...prev,
      [name]: name === 'price' ? Number(value) : value
    }));
  };

  // Add validation function
  const validateForm = (item: Partial<ServiceItem>) => {
    return (
      item.name?.trim() !== '' &&
      (item.price || 0) > 0 &&
      item.duration?.trim() !== '' &&
      item.description?.trim() !== '' &&
      item.jobDetails?.trim() !== ''
    );
  };

  // Update save function
  const saveNewServiceItem = async () => {
    if (!validateForm(newServiceItem)) {
      alert('Vui lòng điền đầy đủ thông tin cho tất cả các trường');
      return;
    }

    if (!selectedSubcategoryId) return;

    try {
      setAddingServiceItem(true);
      setError(null);

      // Get the current highest order for the subcategory
      const subcategoryItems = serviceItems.filter(item => item.subcategoryId === selectedSubcategoryId);
      const maxOrder = Math.max(...subcategoryItems.map(item => item.order || 0), 0);

      const serviceItemData = {
        ...newServiceItem,
        subcategoryId: selectedSubcategoryId,
        order: maxOrder + 1,
        active: true
      };

      const docRef = await addDoc(collection(db, 'service_items'), serviceItemData);
      
      const newItem: ServiceItem = {
        id: docRef.id,
        ...serviceItemData
      };

      setServiceItems(prev => [...prev, newItem]);
      setFilteredServiceItems(prev => [...prev, newItem]);
      closeAddModal();

    } catch (error: any) {
      console.error('Lỗi khi thêm gói dịch vụ mới:', error);
      setError('Không thể thêm gói dịch vụ mới. Vui lòng thử lại.');
    } finally {
      setAddingServiceItem(false);
    }
  };

  const updateServiceItemOrder = async (serviceItemId: string, newOrder: number) => {
    try {
      setUpdating(serviceItemId);
      const serviceItemRef = doc(db, 'service_items', serviceItemId);
      await updateDoc(serviceItemRef, {
        order: newOrder
      });

      // Cập nhật state local
      const updatedServiceItems = serviceItems.map(serviceItem =>
        serviceItem.id === serviceItemId
          ? { ...serviceItem, order: newOrder }
          : serviceItem
      );
      
      // Sắp xếp lại theo order
      updatedServiceItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setServiceItems(updatedServiceItems);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedServiceItems);
      setFilteredServiceItems(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật thứ tự:', error);
      setError('Cập nhật thứ tự thất bại');
    } finally {
      setUpdating(null);
    }
  };

  const toggleActive = async (serviceItemId: string, currentActive: boolean) => {
    try {
      setUpdating(serviceItemId);
      const serviceItemRef = doc(db, 'service_items', serviceItemId);
      await updateDoc(serviceItemRef, {
        active: !currentActive
      });

      // Cập nhật state local
      const updatedServiceItems = serviceItems.map(item =>
        item.id === serviceItemId
          ? { ...item, active: !currentActive }
          : item
      );
      
      setServiceItems(updatedServiceItems);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedServiceItems);
      setFilteredServiceItems(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái hiển thị:', error);
      setError('Cập nhật trạng thái hiển thị thất bại');
    } finally {
      setUpdating(null);
    }
  };

  // Functions for edit modal
  const openEditModal = (serviceItem: ServiceItem) => {
    setEditingServiceItem(serviceItem);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingServiceItem(null);
    setIsEditModalOpen(false);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (editingServiceItem) {
      setEditingServiceItem({
        ...editingServiceItem,
        [name]: name === 'price' ? Number(value) : value
      });
    }
  };

  const saveEditedServiceItem = async () => {
    if (!editingServiceItem || !validateForm(editingServiceItem)) {
      alert('Vui lòng điền đầy đủ thông tin cho tất cả các trường');
      return;
    }

    setUpdatingServiceItem(true);
    try {
      const serviceItemRef = doc(db, 'service_items', editingServiceItem.id);
      await updateDoc(serviceItemRef, {
        name: editingServiceItem.name,
        price: editingServiceItem.price,
        duration: editingServiceItem.duration,
        description: editingServiceItem.description,
        jobDetails: editingServiceItem.jobDetails
      });

      // Update local state
      setServiceItems(prevItems =>
        prevItems.map(item =>
          item.id === editingServiceItem.id ? editingServiceItem : item
        )
      );

      closeEditModal();
    } catch (error) {
      console.error('Lỗi khi cập nhật gói dịch vụ:', error);
      setError('Không thể cập nhật gói dịch vụ');
    } finally {
      setUpdatingServiceItem(false);
    }
  };

  // Effect để fetch data lần đầu
  useEffect(() => {
    const fetchData = async () => {
      await fetchSubcategories();
      await fetchServiceItems();
    };
    fetchData();
  }, []);

  // Memoized filtered items
  const memoFilteredServiceItems = useMemo(() => applyFilters(serviceItems), [serviceItems, subcategories, nameFilter]);

  useEffect(() => {
    setFilteredServiceItems(memoFilteredServiceItems);
  }, [memoFilteredServiceItems]);

  // Memoized grouping and sorting
  const groupedServiceItems = useMemo(() => groupServiceItemsBySubcategory(memoFilteredServiceItems), [memoFilteredServiceItems]);
  const sortedSubcategoryIds = useMemo(() => {
    const ids = Object.keys(groupedServiceItems);
    ids.sort((a, b) => {
      const orderA = subcategories.find(sub => sub.id === a)?.order || 0;
      const orderB = subcategories.find(sub => sub.id === b)?.order || 0;
      return orderA - orderB;
    });
    return ids;
  }, [groupedServiceItems, subcategories]);

  const showMoreInGroup = (subcategoryId: string) => {
    setGroupShowCounts(prev => ({
      ...prev,
      [subcategoryId]: (prev[subcategoryId] || defaultShowPerGroup) + defaultShowPerGroup
    }));
  };

  if (loading) {
    return (
      <div className="manage-service-items">
        <div className="page-header">
          <h2>Quản lý gói dịch vụ</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả gói dịch vụ theo từng dịch vụ</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  

  return (
    <div className="manage-service-items">
      <div className="page-header">
        <h2>Quản lý gói dịch vụ</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả gói dịch vụ theo từng dịch vụ</p>
        <p className="edit-instruction">💡 Click vào các trường hoặc icon ✏️ để chỉnh sửa. Nhấn Enter hoặc click ra ngoài để lưu, Escape để hủy.</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="name-filter">Tìm kiếm:</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Tìm theo tên, mô tả, dịch vụ..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <button onClick={() => {
          fetchSubcategories();
          fetchServiceItems();
        }} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchSubcategories();
              fetchServiceItems();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="grouped-container">
        {sortedSubcategoryIds.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {serviceItems.length === 0 ? 'Không có gói dịch vụ nào' : 'Không tìm thấy gói dịch vụ phù hợp'}
            </div>
          </div>
        ) : (
          sortedSubcategoryIds.map((subcategoryId) => {
            const subcategoryServiceItems = groupedServiceItems[subcategoryId];
            const subcategoryName = getSubcategoryName(subcategoryId);
            const isCollapsed = collapsedGroups.has(subcategoryId);
            
            return (
              <div key={subcategoryId} className="subcategory-group" data-subcategory-id={subcategoryId}>
                <div 
                  className="subcategory-header"
                  onClick={() => toggleGroupCollapse(subcategoryId)}
                >
                  <div className="subcategory-info">
                    <h3 className="subcategory-title">{subcategoryName}</h3>
                    <span className="subcategory-count">({subcategoryServiceItems.length} gói dịch vụ)</span>
                  </div>
                  <div className="header-actions">
                    <button
                      className="add-service-item-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddModal(subcategoryId);
                      }}
                      title="Thêm gói dịch vụ mới"
                    >
                      + Thêm gói
                    </button>
                    <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                  </div>
                </div>
                
                {!isCollapsed && (
                  <div className="table-container">
                    <table className="service-items-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Tên gói dịch vụ</th>
                          <th>Giá</th>
                          <th>Thời lượng</th>
                          <th>Mô tả</th>
                          <th>Chi tiết công việc</th>
                          <th>Sắp xếp</th>
                          <th>Hiển thị</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(subcategoryServiceItems.slice(0, groupShowCounts[subcategoryId] || defaultShowPerGroup)).map((serviceItem, index) => (
                          <tr key={serviceItem.id}>
                            <td className="order-cell">
                              <span className="order-display">{index + 1}</span>
                            </td>
                            <td className="service-item-name">
                              <span
                                className="service-item-name-text clickable"
                                onClick={() => openEditModal(serviceItem)}
                                title="Chỉnh sửa gói dịch vụ"
                                style={{ cursor: 'pointer', flex: 1 }}
                              >
                                {serviceItem.name}
                              </span>
                              <button 
                                className="edit-icon-btn" 
                                onClick={() => openEditModal(serviceItem)}
                                title="Chỉnh sửa gói dịch vụ"
                              >
                                ✏️
                              </button>
                            </td>
                            <td className="service-item-price">
                              {formatPrice(serviceItem.price)}
                            </td>
                            <td>
                              {serviceItem.duration}
                            </td>
                            <td>
                              {serviceItem.description}
                            </td>
                            <td>
                              {serviceItem.jobDetails}
                            </td>
                            <td>
                              <input
                                type="number"
                                value={serviceItem.order || 0}
                                onChange={(e) => updateServiceItemOrder(serviceItem.id, parseInt(e.target.value) || 0)}
                                className="order-input"
                                min="0"
                                placeholder="Thứ tự"
                              />
                              {updating === serviceItem.id && (
                                <span className="updating">Đang cập nhật...</span>
                              )}
                            </td>
                            <td>
                              <div className="toggle-container">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={serviceItem.active ?? true}
                                    onChange={() => toggleActive(serviceItem.id, serviceItem.active ?? true)}
                                    disabled={updating === serviceItem.id}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {subcategoryServiceItems.length > (groupShowCounts[subcategoryId] || defaultShowPerGroup) && (
                      <div style={{ padding: '12px', textAlign: 'center' }}>
                        <button className="refresh-button" onClick={() => showMoreInGroup(subcategoryId)}>Tải thêm</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Service Item Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Thêm Gói Dịch vụ Mới</h2>
              <button onClick={closeAddModal} className="close-button">&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Tên gói dịch vụ:</label>
                <input
                  name="name"
                  value={newServiceItem.name}
                  onChange={handleInputChange}
                  placeholder="Nhập tên gói dịch vụ"
                  required
                />
              </div>
              <div className="form-group">
                <label>Giá:</label>
                <input
                  name="price"
                  type="number"
                  value={newServiceItem.price}
                  onChange={handleInputChange}
                  placeholder="Nhập giá gói dịch vụ"
                  required
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Thời lượng:</label>
                <input
                  name="duration"
                  value={newServiceItem.duration}
                  onChange={handleInputChange}
                  placeholder="Ví dụ: 2 giờ, 90 phút"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mô tả:</label>
                <textarea
                  name="description"
                  value={newServiceItem.description}
                  onChange={handleInputChange}
                  placeholder="Nhập mô tả gói dịch vụ"
                  required
                />
              </div>
              <div className="form-group">
                <label>Chi tiết công việc:</label>
                <textarea
                  name="jobDetails"
                  value={newServiceItem.jobDetails}
                  onChange={handleInputChange}
                  placeholder="Nhập chi tiết công việc"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={closeAddModal}
                disabled={addingServiceItem}
              >
                Hủy
              </button>
              <button
                className="save-button"
                onClick={saveNewServiceItem}
                disabled={addingServiceItem || !validateForm(newServiceItem)}
              >
                {addingServiceItem ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Edit Modal */}
      {isEditModalOpen && editingServiceItem && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Chỉnh sửa gói dịch vụ</h2>
              <button className="close-button" onClick={closeEditModal}>&times;</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Tên gói dịch vụ:</label>
                <input
                  name="name"
                  value={editingServiceItem.name}
                  onChange={handleEditInputChange}
                  placeholder="Nhập tên gói dịch vụ"
                  required
                />
              </div>
              <div className="form-group">
                <label>Giá:</label>
                <input
                  name="price"
                  type="number"
                  value={editingServiceItem.price}
                  onChange={handleEditInputChange}
                  placeholder="Nhập giá gói dịch vụ"
                  required
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Thời lượng:</label>
                <input
                  name="duration"
                  value={editingServiceItem.duration}
                  onChange={handleEditInputChange}
                  placeholder="Ví dụ: 2 giờ, 90 phút"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mô tả:</label>
                <textarea
                  name="description"
                  value={editingServiceItem.description}
                  onChange={handleEditInputChange}
                  placeholder="Nhập mô tả gói dịch vụ"
                  required
                />
              </div>
              <div className="form-group">
                <label>Chi tiết công việc:</label>
                <textarea
                  name="jobDetails"
                  value={editingServiceItem.jobDetails}
                  onChange={handleEditInputChange}
                  placeholder="Nhập chi tiết công việc"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={closeEditModal}
                disabled={updatingServiceItem}
              >
                Hủy
              </button>
              <button
                className="save-button"
                onClick={saveEditedServiceItem}
                disabled={updatingServiceItem || !validateForm(editingServiceItem)}
              >
                {updatingServiceItem ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageServiceItems; 